/**
 * 波A · 长图模板墙(§3.8 换壳后的唯一画廊)。
 *
 * 两个挂载点共用:`/ai-haibao/templates` 页 + 舱内页「从模板开始」弹层(定案 3.8.1:
 * 不做第四种壳,同一份 gallery 组件)。筛选 = **用途(10)+ 热门/最新/常用** ——
 * ratio/tier/provider 删(对长图模板无意义:ratio/tier 是 P5 占位列,provider 不进 schema);
 * 设计稿列的「行业」筛选在租户侧**故意不做**:gallery 本来就只返本行业+通用,
 * 租户筛不出别的行业,摆一个永远只有一两项的筛选器是噪音(admin 列表才有行业筛)。
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LayoutTemplate } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { Pagination } from "@/shared/ui/pagination";
import { USE_CASES } from "../../../longimage/model/blocks";
import { parseTemplateSchema } from "../../../longimage/model/templateSchema";
import {
  posterTemplateService,
  type PosterTemplateBrief,
} from "../../../api/posterTemplateService";
import { TemplateCard } from "./TemplateCard";

const PAGE_SIZE = 12;
// ★不提供「常用」:recent_uses 在后端是零写入者(全仓无 increment,旧套模板流也没记),
//   挂一个永远不变的排序 = 对用户撒谎。等模板使用计数真的有写入方(路线图)再上。
const SORTS = [
  { key: "hot", label: "热门" },
  { key: "new", label: "最新" },
] as const;

interface Props {
  onPick: (t: PosterTemplateBrief) => void;
  /** 弹层用:压缩尺寸(单页 6 张、不显用途全集)。 */
  compact?: boolean;
}

export function LongImageTemplateWall({ onPick, compact = false }: Props) {
  const [usage, setUsage] = useState<string>("all");
  const [sort, setSort] = useState<(typeof SORTS)[number]["key"]>("hot");
  const [page, setPage] = useState(1);
  const size = compact ? 6 : PAGE_SIZE;

  useEffect(() => setPage(1), [usage, sort]);

  const galleryQ = useQuery({
    queryKey: ["poster-longimage-gallery", { usage, sort, page, size }],
    queryFn: () =>
      posterTemplateService.listGallery({
        scene: usage === "all" ? undefined : usage,
        sort,
        page,
        size,
      }),
  });
  /**
   * ★2026-08-12:横宽宽图入口收起(POSTER_HIDDEN_CARD_KEYS)后剩下的第二条路 ——
   * 这面墙照样摆 direction="h" 的模板,点一张就把搭建器开成横宽。卡收了、模板还挑得到
   * 等于没收,所以用户侧挑选面一并过滤。
   *
   * ★只收**用户侧**:admin 模板编辑器、既有横宽草稿、深链、历史作品一律不动
   *   (与卡墙同一口径 —— 是隐藏不是删)。放回来 = 删掉这个过滤。
   * ★代价如实记:total 仍是服务端未过滤的条数,分页里被滤掉的那几张会让本页少几张卡。
   *   等横宽正式退役/恢复再定是否下推到服务端筛,不为一个临时收起改接口契约。
   */
  const templates = (galleryQ.data?.records ?? []).filter(
    (t) => parseTemplateSchema(t.schemaJson)?.canvas.direction !== "h"
  );
  const total = galleryQ.data?.total ?? 0;

  return (
    <div className="space-y-4" data-longimage-template-wall>
      <div className="flex flex-wrap items-center gap-1.5">
        {[{ key: "all", label: "全部用途" }, ...USE_CASES].map((u) => (
          <button
            key={u.key}
            type="button"
            aria-pressed={usage === u.key}
            onClick={() => setUsage(u.key)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              usage === u.key
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border/60 bg-card/40 text-muted-foreground hover:text-foreground"
            )}
          >
            {u.label}
          </button>
        ))}
        <span className="ml-auto inline-flex gap-1 rounded-full border border-border/60 bg-background/40 p-0.5">
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              aria-pressed={sort === s.key}
              onClick={() => setSort(s.key)}
              className={cn(
                "rounded-full px-2.5 py-0.5 text-2xs font-medium transition-colors",
                sort === s.key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {s.label}
            </button>
          ))}
        </span>
      </div>

      {galleryQ.isError ? (
        <div className="flex flex-col items-center gap-2 rounded-[calc(var(--radius)*1.4)] border border-dashed border-destructive/40 py-14 text-center">
          <p className="text-sm font-medium">模板库加载失败</p>
          <p className="text-xs text-muted-foreground">
            网络或服务暂时不可用 —— 失败不是「没有模板」。
          </p>
          <button
            type="button"
            onClick={() => galleryQ.refetch()}
            className="rounded-full border border-border/60 px-3 py-1 text-xs font-medium hover:border-primary/60"
          >
            重试
          </button>
        </div>
      ) : galleryQ.isLoading ? (
        <div
          className={cn("grid gap-4", compact ? "sm:grid-cols-3" : "sm:grid-cols-3 xl:grid-cols-4")}
        >
          {Array.from({ length: compact ? 3 : 8 }).map((_, i) => (
            <div
              key={i}
              aria-hidden
              className="h-56 animate-pulse rounded-[calc(var(--radius)*1.4)] border border-border/40 bg-card/40"
            />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-[calc(var(--radius)*1.4)] border border-dashed border-border/60 py-14 text-center">
          <LayoutTemplate className="h-8 w-8 text-muted-foreground/60" strokeWidth={1.5} />
          <p className="text-sm font-medium">这个用途下还没有长图模板</p>
          <p className="text-xs text-muted-foreground">
            换个用途看看,或从创意设计中心的用途卡直接搭 —— 模板由平台持续上新。
          </p>
        </div>
      ) : (
        <div
          className={cn("grid gap-4", compact ? "sm:grid-cols-3" : "sm:grid-cols-3 xl:grid-cols-4")}
        >
          {templates.map((t) => (
            <TemplateCard key={t.id} tpl={t} onApply={onPick} />
          ))}
        </div>
      )}

      {!compact && total > size && (
        // 共享分页器是 0-based;后端 gallery 语义 1-based —— 实证 gscq-api
        // PosterTemplateService.paginate:`p = Math.max(1, page); from = (p-1)*size`,
        // 即 page=1 就是第一页(codex 三轮坚持「后端 0-based」均为误报;倒是旧前端传
        // 0-based 时「第 2 页(=1)与第 1 页重复」才是潜伏 bug,模板少从没翻过页所以没炸)
        <Pagination
          page={page - 1}
          pageSize={size}
          total={total}
          onPageChange={(p) => setPage(p + 1)}
          showJump={false}
        />
      )}
    </div>
  );
}
